/**
 * Native push (Capacitor PushNotifications). Runs only on Android / iOS.
 *
 * Lifecycle:
 *   1. `init()` — requests permission, registers with FCM, wires listeners.
 *      Safe to call multiple times; the second call is a no-op.
 *   2. On `registration` event, the FCM token is saved to RTDB under
 *      `users/{uid}/fcmTokens/{token}`.
 *   3. On `pushNotificationReceived` (foreground), the message is deduped and
 *      passed to the `onMessage` callback.
 *   4. On `pushNotificationActionPerformed` (user tapped a background push),
 *      same callback fires with `tapped: true`.
 */

import { auth, ref } from '../../core/firebase.js';
import { LocalState } from '../../core/localState.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('push.native');

const DEDUP_WINDOW_MS = 10 * 60 * 1000;
/** @type {Map<string, number>} */
const seen = new Map();

let registered = false;
/** @type {((notification: any) => void) | null} */
let onMessageHandler = null;

/** @returns {boolean} */
export function isAvailable() {
  const cap = /** @type {any} */ (window).Capacitor;
  return !!(cap?.Plugins?.PushNotifications && cap?.isNativePlatform?.());
}

/**
 * @param {{ onMessage?: (notification: any) => void }} [hooks]
 * @returns {Promise<boolean>}  true if registration succeeded
 */
export async function init(hooks = {}) {
  if (!isAvailable()) {
    log.info('not a native platform — skipping native push');
    return false;
  }
  if (registered) return true;
  registered = true;
  onMessageHandler = hooks.onMessage ?? null;

  const Push = /** @type {any} */ (window).Capacitor.Plugins.PushNotifications;
  try {
    // CRITICAL ORDERING: addListener BEFORE register(). The registration event
    // can fire before `register()`'s promise resolves on some devices (cached
    // FCM token), so attaching the listener afterwards would lose the token.
    Push.addListener('registration', (/** @type {{ value: string }} */ res) => {
      log.info('FCM token received (length=' + (res?.value?.length ?? 0) + ')');
      void saveToken(res.value);
    });
    Push.addListener('registrationError', (/** @type {any} */ err) => {
      log.warn('registration error:', err);
    });
    Push.addListener('pushNotificationReceived', (/** @type {any} */ notif) => {
      handleIncoming(notif, false);
    });
    Push.addListener('pushNotificationActionPerformed', (/** @type {any} */ action) => {
      handleIncoming(action?.notification ?? {}, true);
    });

    let perm = await Push.checkPermissions();
    log.info('initial permission:', perm.receive);
    if (perm.receive !== 'granted') {
      const requested = await Push.requestPermissions();
      perm = requested;
      log.info('after request permission:', perm.receive);
    }
    if (perm.receive !== 'granted') {
      log.info('permission denied by user');
      return false;
    }

    log.info('calling Push.register()');
    await Push.register();
    log.info('Push.register() resolved');

    return true;
  } catch (err) {
    log.warn('init failed:', err);
    return false;
  }
}

/** @type {string | null} */
let pendingToken = null;
let authRetryArmed = false;

/**
 * Save the FCM token to `users/{uid}/fcmTokens/{token}`. If auth isn't ready
 * yet (e.g. the registration listener fires before the anonymous sign-in
 * completes), cache the token and write it via an `onAuthStateChanged` hook
 * the first time a user appears.
 *
 * @param {string} token
 */
export async function saveToken(token) {
  if (!token) return;
  LocalState.fcmToken = token;

  const uid = auth().currentUser?.uid;
  if (!uid) {
    log.info('no auth uid yet — queuing token for save when auth is ready');
    pendingToken = token;
    armAuthRetry();
    return;
  }

  try {
    const platform = getPlatform();
    // De-duplicate: drop any other native tokens for the same user before
    // registering the new one. FCM rotates tokens between APK launches
    // and without pruning we'd accumulate 2-3 active tokens → user gets
    // multiple pushes per message.
    const { db } = await import('../../core/firebase.js');
    const snap = await ref(`users/${uid}/fcmTokens`).once('value');
    const tokens = snap.val() || {};
    /** @type {Record<string, any>} */
    const updates = {};
    for (const [t, meta] of Object.entries(tokens)) {
      if (t === token) continue;
      if (meta && /** @type {any} */ (meta).platform === platform) {
        updates[`users/${uid}/fcmTokens/${t}`] = null;
      }
    }
    updates[`users/${uid}/fcmTokens/${token}`] = { at: Date.now(), platform };
    await db().ref().update(updates);
    log.info('FCM token saved + stale pruned under users/' + uid + '/fcmTokens');
    pendingToken = null;
  } catch (err) {
    log.warn('saveToken failed:', err);
  }
}

function armAuthRetry() {
  if (authRetryArmed) return;
  authRetryArmed = true;
  const a = auth();
  if (!a.onAuthStateChanged) return;
  const unsub = a.onAuthStateChanged(async (/** @type {any} */ user) => {
    if (user && pendingToken) {
      log.info('auth ready — writing previously-cached FCM token');
      const t = pendingToken;
      pendingToken = null;
      try { unsub(); } catch { /* ignore */ }
      await saveToken(t);
    }
  });
}

/**
 * @param {any} notif
 * @param {boolean} tapped
 */
function handleIncoming(notif, tapped) {
  const data = notif?.data ?? {};
  if (!shouldProcess(data)) {
    log.debug('duplicate push — skipping');
    return;
  }
  if (onMessageHandler) {
    try { onMessageHandler({ ...notif, tapped }); }
    catch (e) { log.warn('onMessage handler threw:', e); }
  }
}

/**
 * Dedup window — push messages can arrive multiple times (foreground +
 * background, retries). Drop anything we've seen in the last 10 minutes.
 * @param {{ id?: string; messageId?: string; convId?: string; msgId?: string }} data
 */
export function shouldProcess(data) {
  const id = String(
    data?.id ?? data?.messageId ?? (data?.convId && data?.msgId ? `${data.convId}/${data.msgId}` : ''),
  );
  if (!id) return true;
  prune();
  if (seen.has(id)) return false;
  seen.set(id, Date.now());
  return true;
}

function prune() {
  const cutoff = Date.now() - DEDUP_WINDOW_MS;
  for (const [id, at] of seen) {
    if (at < cutoff) seen.delete(id);
  }
}

/** @returns {'android' | 'ios' | 'web'} */
function getPlatform() {
  const cap = /** @type {any} */ (window).Capacitor;
  const p = cap?.getPlatform?.();
  if (p === 'android' || p === 'ios') return p;
  return 'web';
}
